// Minimax with alpha-beta pruning. The bot is BOT (yellow), maximizing.
// Move ordering "from the centre out" makes pruning cut aggressively, so depth 5
// on a 7-wide tree returns in tens of ms — synchronous, no worker.
import { Cell, EMPTY, HUMAN, BOT, COLS, ROWS, idx, AI_DEPTH } from './types';

const ORDER = [3, 2, 4, 1, 5, 0, 6];   // centre-first column order

function dropRow(b: Cell[], col: number): number {
  for (let r = 0; r < ROWS; r++) if (b[idx(col, r)] === EMPTY) return r;
  return -1;
}

/**
 * Did placing `cell` at (col,row) complete a line of 4? (counts from that cell).
 * Намеренно дублирует logику checkWinAt из game.ts (сканит 4 оси в обе стороны),
 * но возвращает bool вместо массива индексов — в горячей дорожке minimax ненужна
 * аллокация массива.
 */
function winsAt(b: Cell[], col: number, row: number, cell: Cell): boolean {
  const dirs: ReadonlyArray<[number, number]> = [[1, 0], [0, 1], [1, 1], [1, -1]];
  for (const [dc, dr] of dirs) {
    let n = 1;
    let c = col + dc, r = row + dr;
    while (c >= 0 && c < COLS && r >= 0 && r < ROWS && b[idx(c, r)] === cell) { n++; c += dc; r += dr; }
    c = col - dc; r = row - dr;
    while (c >= 0 && c < COLS && r >= 0 && r < ROWS && b[idx(c, r)] === cell) { n++; c -= dc; r -= dr; }
    if (n >= 4) return true;
  }
  return false;
}

function scoreWindow(bot: number, hum: number, emp: number): number {
  if (bot === 4) return 100000;
  if (bot === 3 && emp === 1) return 50;
  if (bot === 2 && emp === 2) return 10;
  if (hum === 3 && emp === 1) return -80;  // blocking a threat beats building own pairs
  if (hum === 2 && emp === 2) return -10;
  return 0;
}

/** Heuristic leaf evaluation from the bot's perspective (positive = good for bot). */
export function evaluate(b: Cell[]): number {
  let score = 0;
  for (let r = 0; r < ROWS; r++) if (b[idx(3, r)] === BOT) score += 6;  // centre control

  const win = (a: number, c: number, d: number, e: number): void => {
    let bot = 0, hum = 0, emp = 0;
    for (const i of [a, c, d, e]) {
      const v = b[i];
      if (v === BOT) bot++; else if (v === HUMAN) hum++; else emp++;
    }
    score += scoreWindow(bot, hum, emp);
  };

  for (let r = 0; r < ROWS; r++)
    for (let c = 0; c <= COLS - 4; c++)
      win(idx(c, r), idx(c + 1, r), idx(c + 2, r), idx(c + 3, r));          // ─
  for (let c = 0; c < COLS; c++)
    for (let r = 0; r <= ROWS - 4; r++)
      win(idx(c, r), idx(c, r + 1), idx(c, r + 2), idx(c, r + 3));          // │
  for (let c = 0; c <= COLS - 4; c++)
    for (let r = 0; r <= ROWS - 4; r++)
      win(idx(c, r), idx(c + 1, r + 1), idx(c + 2, r + 2), idx(c + 3, r + 3)); // ╱
  for (let c = 0; c <= COLS - 4; c++)
    for (let r = 3; r < ROWS; r++)
      win(idx(c, r), idx(c + 1, r - 1), idx(c + 2, r - 2), idx(c + 3, r - 3)); // ╲

  return score;
}

function minimax(b: Cell[], depth: number, alpha: number, beta: number, maximizing: boolean): number {
  const cols: number[] = [];
  for (const c of ORDER) if (b[idx(c, ROWS - 1)] === EMPTY) cols.push(c);
  if (cols.length === 0) return 0;        // full board → draw
  if (depth === 0) return evaluate(b);

  const ply = AI_DEPTH - depth;           // nearer wins score higher

  if (maximizing) {
    let value = -Infinity;
    for (const c of cols) {
      const r = dropRow(b, c); const i = idx(c, r);
      b[i] = BOT;
      const s = winsAt(b, c, r, BOT) ? 1_000_000 - ply : minimax(b, depth - 1, alpha, beta, false);
      b[i] = EMPTY;
      if (s > value) value = s;
      if (value > alpha) alpha = value;
      if (alpha >= beta) break;
    }
    return value;
  }
  let value = Infinity;
  for (const c of cols) {
    const r = dropRow(b, c); const i = idx(c, r);
    b[i] = HUMAN;
    const s = winsAt(b, c, r, HUMAN) ? -1_000_000 + ply : minimax(b, depth - 1, alpha, beta, true);
    b[i] = EMPTY;
    if (s < value) value = s;
    if (value < beta) beta = value;
    if (alpha >= beta) break;
  }
  return value;
}

/**
 * Best column for the bot. Immediate wins are caught at the top level; immediate
 * threats are blocked because the recursion sees the human's winning reply as a
 * large negative. Ties keep the earlier (more central) column.
 */
export function bestMove(board: Cell[]): number {
  const b = board.slice();
  let best = ORDER.find((c) => b[idx(c, ROWS - 1)] === EMPTY) ?? 3;
  let bestScore = -Infinity;
  for (const c of ORDER) {
    if (b[idx(c, ROWS - 1)] !== EMPTY) continue;
    const r = dropRow(b, c); const i = idx(c, r);
    b[i] = BOT;
    const s = winsAt(b, c, r, BOT) ? 1_000_000 : minimax(b, AI_DEPTH - 1, -Infinity, Infinity, false);
    b[i] = EMPTY;
    if (s > bestScore) { bestScore = s; best = c; }
  }
  return best;
}
